import { useTheme } from "@emotion/react";
import useStyles from "./styles";
import { useOutletContext } from "react-router-dom";
import {
	Box,
	Chip,
	Grid,
	LinearProgress,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import CardGraphComponent from "../../../../components/cardGraphComponent";
import {
	BoltOutlined,
	DownloadOutlined,
	FlashOnOutlined,
	PublishOutlined,
	StorageOutlined,
	ThermostatOutlined,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import DisponibilidadeApiComponent from "../../../internal/3.0-dashboard/3.5-transceptor/statusBars";
import corPorTrigger from "../../../../utils/corPorTrigger";
import ProblemasTrigger from "../../../../components/problemasTrigger";

export default function DashSfpCliente() {
	const {
		filterData,
		handleAplicarZoom,
		setLoadingCronometro,
		setCounter,
		selectTime,
		optionsTime,
		startCronometro,
		controllerDashboard,
		setControllerDashboard,
		toggleDrawer,
		apiCliente,
		stopCronometro,
	} = useOutletContext();
	const theme = useTheme();
	const styles = useStyles(theme);
	const [loading, setLoading] = useState({
		current: false,
		receivedPower: false,
		transmittedPower: false,
		ifStatus: false,
		temperature: false,
		voltage: false,
	});
	// Status das triggers do Zabbix por itemid, que define a cor dos cards.
	// null = ainda não consultado, ou consulta falhou → cards neutros.
	const [statusTriggers, setStatusTriggers] = useState(null);
	const [data, setData] = useState({
		current: {
			currentValue: 0,
			data: [],
		},
		receivedPower: {
			currentValue: 0,
			data: [],
		},
		transmittedPower: {
			currentValue: 0,
			data: [],
		},
		ifStatus: {
			currentValue: 0,
			data: {
				history: [],
				periodLabels: [],
				interval: 0,
				groupType: "",
			},
		},
		temperature: {
			currentValue: 0,
			data: [],
		},
		voltage: {
			currentValue: 0,
			data: [],
		},
	});

	const clearData = () => {
		setData({
			current: {
				currentValue: 0,
				data: [],
			},
			receivedPower: {
				currentValue: 0,
				data: [],
			},
			transmittedPower: {
				currentValue: 0,
				data: [],
			},
			ifStatus: {
				currentValue: 0,
				data: {
					history: [],
					periodLabels: [],
					interval: 0,
					groupType: "",
				},
			},
			temperature: {
				currentValue: 0,
				data: [],
			},
			voltage: {
				currentValue: 0,
				data: [],
			},
		});
	};

	useEffect(() => {
		if (
			filterData?.interfaceId &&
			filterData?.hostId &&
			filterData?.initialDate &&
			filterData?.finalDate
		) {
			fetchAllData();
			toggleDrawer(false)();
		} else {
			clearData();
		}
	}, [filterData]);

	useEffect(() => {
		return () => {
			if (controllerDashboard) {
				controllerDashboard.abort();
			}

			stopCronometro();
		};
	}, []);

	const fetchAllData = async () => {
		setCounter(null);
		stopCronometro();
		setLoadingCronometro(true);
		let dateInitial = filterData?.initialDate;
		let finalDate = filterData?.finalDate;

		if (selectTime || selectTime === 0) {
			dateInitial = optionsTime[selectTime]?.value;
			finalDate = dayjs()?.set("second", 0)?.set("millisecond", 0)?.toDate();
		}
		if (controllerDashboard) {
			controllerDashboard.abort();
		}
		const newController = new AbortController();
		const signal = newController.signal;
		setControllerDashboard(newController);

		const itemids = await Promise.all([
			getReceivedPower(dateInitial, finalDate, signal),
			getTransmittedPower(dateInitial, finalDate, signal),
			getCurrent(dateInitial, finalDate, signal),
			getTemperature(dateInitial, finalDate, signal),
			getVoltage(dateInitial, finalDate, signal),
			getIfStatus(dateInitial, finalDate, signal),
		]);

		// Cor dos cards vem das triggers do Zabbix dos itens exibidos
		if (!signal.aborted) {
			await getStatusTriggers(itemids?.filter(Boolean), signal);
		}
		if (!signal.aborted) {
			setLoadingCronometro(false);
			startCronometro();
		}
	};

	const getReceivedPower = async (dateInitial, finalDate, signal) => {
		loading.receivedPower = true;
		try {
			const [receivedPowerHistory, lastReceivedPower] = await Promise.all([
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						initial_date: dateInitial,
						final_date: finalDate,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "receivedPower",
						exactMatch: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "receivedPower",
						exactMatch: true,
						lastItem: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
			]);

			setData((prevData) => ({
				...prevData,
				receivedPower: {
					currentValue: lastReceivedPower?.data?.value || 0,
					data: receivedPowerHistory?.data || [],
					itemid: lastReceivedPower?.data?.itemid || null,
				},
			}));

			// Id do item para cruzar com o status das triggers
			return lastReceivedPower?.data?.itemid;
		} catch (error) {
			console.error(error);
		} finally {
			loading.receivedPower = false;
		}
	};

	const getTransmittedPower = async (dateInitial, finalDate, signal) => {
		loading.transmittedPower = true;
		try {
			const [transmittedPowerHistory, lastTransmittedPower] = await Promise.all(
				[
					apiCliente.post(
						"/cliente/zabbix/serieTemporal",
						{
							device: filterData?.device,
							initial_date: dateInitial,
							final_date: finalDate,
							interface: filterData?.interfaceId,
							hostids: filterData?.hostId,
							nameFilter: "transmittedPower",
							exactMatch: true,
							fabricante: filterData?.fabricante,
							funcao: filterData?.funcao,
							modelo: filterData?.modelo,
							dash: "sfp",
						},
						{ signal },
					),
					apiCliente.post(
						"/cliente/zabbix/serieTemporal",
						{
							device: filterData?.device,
							interface: filterData?.interfaceId,
							hostids: filterData?.hostId,
							nameFilter: "transmittedPower",
							exactMatch: true,
							lastItem: true,
							fabricante: filterData?.fabricante,
							funcao: filterData?.funcao,
							modelo: filterData?.modelo,
							dash: "sfp",
						},
						{ signal },
					),
				],
			);

			setData((prevData) => ({
				...prevData,
				transmittedPower: {
					currentValue: lastTransmittedPower?.data?.value || 0,
					data: transmittedPowerHistory?.data || [],
					itemid: lastTransmittedPower?.data?.itemid || null,
				},
			}));

			// Id do item para cruzar com o status das triggers
			return lastTransmittedPower?.data?.itemid;
		} catch (error) {
			console.error(error);
		} finally {
			loading.transmittedPower = false;
		}
	};

	const getCurrent = async (dateInitial, finalDate, signal) => {
		loading.current = true;
		try {
			const [currentHistory, lastCurrent] = await Promise.all([
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						initial_date: dateInitial,
						final_date: finalDate,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "current",
						exactMatch: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "current",
						exactMatch: true,
						lastItem: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
			]);

			setData((prevData) => ({
				...prevData,
				current: {
					currentValue: lastCurrent?.data?.value || 0,
					data: currentHistory?.data || [],
					itemid: lastCurrent?.data?.itemid || null,
				},
			}));

			// Id do item para cruzar com o status das triggers
			return lastCurrent?.data?.itemid;
		} catch (error) {
			console.error(error);
		} finally {
			loading.current = false;
		}
	};

	const getTemperature = async (dateInitial, finalDate, signal) => {
		loading.temperature = true;
		try {
			const [temperatureHistory, lastTemperature] = await Promise.all([
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						initial_date: dateInitial,
						final_date: finalDate,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "temperature",
						exactMatch: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "temperature",
						exactMatch: true,
						lastItem: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
			]);

			setData((prevData) => ({
				...prevData,
				temperature: {
					currentValue: lastTemperature?.data?.value || 0,
					data: temperatureHistory?.data || [],
					itemid: lastTemperature?.data?.itemid || null,
				},
			}));

			// Id do item para cruzar com o status das triggers
			return lastTemperature?.data?.itemid;
		} catch (error) {
			console.error(error);
		} finally {
			loading.temperature = false;
		}
	};

	const getVoltage = async (dateInitial, finalDate, signal) => {
		loading.voltage = true;
		try {
			const [voltageHistory, lastVoltage] = await Promise.all([
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						initial_date: dateInitial,
						final_date: finalDate,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "voltage",
						exactMatch: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "voltage",
						exactMatch: true,
						lastItem: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
			]);

			setData((prevData) => ({
				...prevData,
				voltage: {
					currentValue: lastVoltage?.data?.value || 0,
					data: voltageHistory?.data || [],
					itemid: lastVoltage?.data?.itemid || null,
				},
			}));

			// Id do item para cruzar com o status das triggers
			return lastVoltage?.data?.itemid;
		} catch (error) {
			console.error(error);
		} finally {
			loading.voltage = false;
		}
	};

	const getIfStatus = async (dateInitial, finalDate, signal) => {
		loading.ifStatus = true;
		try {
			const [ifStatusHistory, lastIfStatus] = await Promise.all([
				apiCliente.post(
					"cliente/zabbix/statusHistory",
					{
						initial_date: dayjs()?.subtract(2, "day"),
						final_date: dayjs(),
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "ifStatus",
						groupType: "hour",
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
				apiCliente.post(
					"/cliente/zabbix/serieTemporal",
					{
						device: filterData?.device,
						interface: filterData?.interfaceId,
						hostids: filterData?.hostId,
						nameFilter: "ifStatus",
						exactMatch: true,
						lastItem: true,
						fabricante: filterData?.fabricante,
						funcao: filterData?.funcao,
						modelo: filterData?.modelo,
						dash: "sfp",
					},
					{ signal },
				),
			]);
			setData((prevData) => ({
				...prevData,
				ifStatus: {
					currentValue: lastIfStatus?.data?.value || 0,
					data: ifStatusHistory?.data || {
						history: [],
						periodLabels: [],
						interval: 0,
						groupType: "",
					},
				},
			}));
		} catch (error) {
			console.error(error);
		} finally {
			loading.ifStatus = false;
		}
	};

	const getStatusTriggers = async (itemids, signal) => {
		// Sem itens não há trigger a consultar: todos os cards ficam neutros
		if (!itemids?.length) {
			setStatusTriggers({});
			return;
		}
		try {
			const response = await apiCliente.post(
				"/cliente/zabbix/triggers/status",
				{ itemids },
				{ signal },
			);
			setStatusTriggers(response?.data || null);
		} catch (error) {
			// Falha na consulta: cards neutros em vez de um "ok" falso
			if (!signal?.aborted) {
				console.error(error);
				setStatusTriggers(null);
			}
		}
	};

	// A cor dos cards de gráfico vem das triggers do Zabbix (corPorTrigger),
	// não de limiares fixos por métrica
	const itemsToRender = [
		{
			key: "receivedPower",
			title: "Potência recebida",
			valueUnit: "dBm",
			icon: DownloadOutlined,
			minValue: -40,
		},
		{
			key: "transmittedPower",
			title: "Potência transmitida",
			valueUnit: "dBm",
			icon: PublishOutlined,
			minValue: -40,
		},
		{
			key: "current",
			title: "Corrente",
			valueUnit: "mA",
			icon: BoltOutlined,
		},
		{
			key: "ifStatus",
			title: "Status da interface",
			icon: StorageOutlined,
			graphStatus: true,
			getColor: (value) => (value ? "green" : "red"),
		},
		{
			key: "temperature",
			title: "Temperatura",
			valueUnit: "Cº",
			icon: ThermostatOutlined,
		},
		{
			key: "voltage",
			title: "Tensão",
			valueUnit: "V",
			icon: FlashOnOutlined,
		},
	];

	return (
		<Box sx={styles.container}>
			<Grid container spacing={1}>
				{itemsToRender?.map((item, index) => {
					// Status das triggers do item exibido no card
					const statusItem = statusTriggers?.[data?.[item?.key]?.itemid];
					const corCard = corPorTrigger(statusItem);

					return (
						<Grid item xs={12} md={6} key={index}>
							{!item?.graphStatus ? (
								<CardGraphComponent
									loading={loading?.[item?.key]}
									handleAplicarZoom={handleAplicarZoom}
									isZoomEnabled={true}
									titleGraph={item?.title}
									data={
										Array.isArray(data?.[item?.key]?.data)
											? data?.[item?.key]?.data
											: []
									}
									icon={item?.icon}
									valueCard={
										data?.[item?.key]?.currentValue
											? `${parseFloat(data?.[item?.key]?.currentValue)?.toFixed(
													2,
												)} ${item?.valueUnit || ""}`
											: " - "
									}
									CustomTooltip={(props) => (
										<CustomTooltip
											{...props}
											valueUnit={item?.valueUnit}
											title={item?.title}
										/>
									)}
									tickFormatter={(value) => {
										const numericValue = parseFloat(value);
										if (isNaN(numericValue))
											return `0 ${item?.valueUnit || ""}`;
										return `${numericValue?.toFixed(1)} ${item?.valueUnit || ""}`;
									}}
									iconColorCard={corCard}
									topRightItem={
										<ProblemasTrigger
											problemas={statusItem?.problemas}
											cor={corCard}
										/>
									}
									minValuebps={item?.minValue}
								/>
							) : (
								<Paper sx={styles.statusPaper}>
									{loading?.[item?.key] && (
										<LinearProgress sx={styles.linearProgress} />
									)}
									<Box sx={styles.boxStatus}>
										<Typography sx={styles.statusTitle}>
											{item?.title || "Sem dados"}
										</Typography>
										<Chip
											label={data?.[item?.key]?.currentValue ? "UP" : "DOWN"}
											color={
												data?.[item?.key]?.currentValue ? "success" : "error"
											}
											size="small"
										/>
									</Box>
									<Box sx={styles.statusContent}>
										<DisponibilidadeApiComponent
											dataApisFilter={data?.[item?.key]?.data?.history || []}
											tempo={data?.[item?.key]?.data?.interval || 0}
											dias={data?.[item?.key]?.data?.periodLabels || []}
											groupType={data?.[item?.key]?.data?.groupType || ""}
										/>
									</Box>
								</Paper>
							)}
						</Grid>
					);
				})}
			</Grid>
		</Box>
	);
}

const CustomTooltip = ({
	active,
	payload,
	label,
	valueUnit = "",
	title = "",
}) => {
	const theme = useTheme();
	const styles = useStyles(theme);
	if (!active || !payload || payload.length === 0) return null;

	const { clock, value } = payload?.[0]?.payload;

	return (
		<Box sx={styles.boxTooltip}>
			<Typography fontSize={"14px"}>
				{`Data: ${dayjs?.unix(clock)?.format("DD/MM/YY HH:mm")}`}
			</Typography>

			<Stack direction={"row"} alignItems={"center"} gap={"4px"}>
				<Box sx={styles.customTooltip} />
				<Typography fontSize={"14px"}>
					{title || "Valor"}: {value?.toFixed(2)} {valueUnit}
				</Typography>
			</Stack>
		</Box>
	);
};
